import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Search as SearchIcon,
  Sort as SortIcon,
  Restaurant as RestaurantIcon,
  Warehouse as WarehouseIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE_URL, API_ENDPOINTS } from '../../config/api';
import EditItemDialog from './EditItemDialog';
import StorageItemView, { type ItemCartTransferMode } from './StorageItemView';
import type { CartTransferMode } from './cartTypes';

interface StorageItem {
  id: string;
  name: string;
  description: string;
  category: 'kitchen';
  quantity: number;
  takenQuantity?: number;
  minQuantity: number;
  unit: string;
  location: string;
  supplier: string;
  savikaina?: number;
  pardavimoKaina?: number;
  creator: {
    firstName: string;
    lastName: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface KitchenItemsProps {
  refreshTrigger: number;
  onAddToCart: (item: StorageItem, quantity: number, transferSource?: ItemCartTransferMode) => void;
  viewMode: 'cards' | 'list';
  cartTransferMode?: CartTransferMode;
  onCartTransferModeChange?: (mode: CartTransferMode) => void;
}

const KitchenItems: React.FC<KitchenItemsProps> = ({
  refreshTrigger,
  onAddToCart,
  viewMode,
  cartTransferMode = 'warehouse',
  onCartTransferModeChange
}) => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canTransfer = hasPermission('storage.edit');
  const [items, setItems] = useState<StorageItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');
  const [supplierFilter, setSupplierFilter] = useState<string>('all');
  const [allSuppliers, setAllSuppliers] = useState<string[]>([]);
  const [selectedQuantities, setSelectedQuantities] = useState<{ [key: string]: number }>({});
  const [editItemOpen, setEditItemOpen] = useState(false);
  const [itemToEdit, setItemToEdit] = useState<StorageItem | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<StorageItem | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchItems();
  }, [refreshTrigger, supplierFilter, sortBy, sortOrder]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${API_BASE_URL}/api${API_ENDPOINTS.STORAGE_ITEMS_BY_CATEGORY('kitchen')}?sortBy=${sortBy}&sortOrder=${sortOrder}&supplier=${supplierFilter}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setItems(data.items);
        if (supplierFilter === 'all') {
          const suppliers = Array.from(
            new Set(data.items.map((item: StorageItem) => item.supplier).filter(Boolean))
          ).sort() as string[];
          setAllSuppliers(suppliers);
        }
      } else {
        throw new Error(data.message || 'Failed to fetch items');
      }
    } catch (err) {
      console.error('Error fetching kitchen items:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch items');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortOrder('ASC');
    }
  };

  const handleQuantityChange = (itemId: string, quantity: number) => {
    const item = items.find(i => i.id === itemId);
    const maxQty = cartTransferMode === 'taken'
      ? (item?.takenQuantity ?? 0)
      : (item?.quantity ?? 1);
    setSelectedQuantities(prev => ({
      ...prev,
      [itemId]: Math.max(1, Math.min(quantity, maxQty || 1))
    }));
  };

  const handleAddToCart = (item: StorageItem) => {
    const quantity = selectedQuantities[item.id] || 1;
    onAddToCart(item, quantity, cartTransferMode);
    setSelectedQuantities(prev => {
      const newState = { ...prev };
      delete newState[item.id];
      return newState;
    });
  };

  const handleEditItem = (item: StorageItem) => {
    setItemToEdit(item);
    setEditItemOpen(true);
  };

  const handleItemUpdated = () => {
    fetchItems();
  };

  const handleDeleteItem = (item: StorageItem) => {
    setItemToDelete(item);
    setDeleteConfirmOpen(true);
  };

  const handleTransferModeChange = (_e: React.MouseEvent<HTMLElement>, value: CartTransferMode | null) => {
    if (!value || !onCartTransferModeChange) return;
    onCartTransferModeChange(value);
  };

  const confirmDeleteItem = async () => {
    if (!itemToDelete) return;

    try {
      setDeleteLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/storage/items/${itemToDelete.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to delete item');
      }

      await fetchItems();
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
    } catch (err) {
      console.error('Error deleting item:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete item');
    } finally {
      setDeleteLoading(false);
    }
  };

  const searchLower = searchTerm.toLowerCase();
  const filteredItems = items.filter((item) => {
    const matchesSearch =
      item.name.toLowerCase().includes(searchLower) ||
      item.description?.toLowerCase().includes(searchLower) ||
      item.location?.toLowerCase().includes(searchLower);
    const matchesTakenFilter =
      cartTransferMode !== 'taken' || (item.takenQuantity ?? 0) > 0;
    return matchesSearch && matchesTakenFilter;
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {canTransfer && onCartTransferModeChange && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {cartTransferMode === 'taken'
            ? t('storage.kitchen.returnFromTakenHint')
            : t('storage.kitchen.transferToTakenHint')}
        </Alert>
      )}

      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          placeholder="Ieškoti pagal pavadinimą, aprašymą ar vietą..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
          }}
          sx={{ flexGrow: 1, minWidth: 250 }}
        />

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Tiekėjas</InputLabel>
          <Select
            value={supplierFilter}
            label="Tiekėjas"
            onChange={(e) => setSupplierFilter(e.target.value)}
          >
            <MenuItem value="all">Visi tiekėjai</MenuItem>
            {allSuppliers.map((supplier) => (
              <MenuItem key={supplier} value={supplier}>
                {supplier}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {canTransfer && onCartTransferModeChange && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {t('storage.kitchen.transferModeLabel')}
            </Typography>
            <ToggleButtonGroup
              value={cartTransferMode}
              exclusive
              size="small"
              onChange={handleTransferModeChange}
            >
              <ToggleButton value="warehouse">
                <WarehouseIcon sx={{ mr: 0.5, fontSize: 18 }} />
                {t('storage.kitchen.transferModeWarehouse')}
              </ToggleButton>
              <ToggleButton value="taken">
                <RestaurantIcon sx={{ mr: 0.5, fontSize: 18 }} />
                {t('storage.kitchen.transferModeTaken')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant={sortBy === 'name' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => handleSort('name')}
            startIcon={<SortIcon />}
          >
            Pavadinimas {sortBy === 'name' && (sortOrder === 'ASC' ? '↑' : '↓')}
          </Button>
          <Button
            variant={sortBy === 'quantity' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => handleSort('quantity')}
            startIcon={<SortIcon />}
          >
            Kiekis {sortBy === 'quantity' && (sortOrder === 'ASC' ? '↑' : '↓')}
          </Button>
        </Box>
      </Box>

      <StorageItemView
        items={filteredItems}
        viewMode={viewMode}
        selectedQuantities={selectedQuantities}
        onQuantityChange={handleQuantityChange}
        onAddToCart={handleAddToCart}
        onEdit={handleEditItem}
        onDelete={handleDeleteItem}
        canEdit={hasPermission('storage.edit')}
        showTakenQuantity
        cartTransferMode={cartTransferMode}
        emptyMessage={
          cartTransferMode === 'taken'
            ? t('storage.kitchen.noInTaken')
            : t('storage.kitchen.noItems')
        }
      />

      <EditItemDialog
        open={editItemOpen}
        onClose={() => setEditItemOpen(false)}
        item={itemToEdit}
        onItemUpdated={handleItemUpdated}
      />

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Patvirtinti ištrynimą</DialogTitle>
        <DialogContent>
          <Typography>
            Ar tikrai norite ištrinti prekę "{itemToDelete?.name}"?
            Šis veiksmas negrįžtamas.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>
            Atšaukti
          </Button>
          <Button
            onClick={confirmDeleteItem}
            color="error"
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={20} /> : 'Ištrinti'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KitchenItems;
